import { useEffect, useRef, useState } from "react";

import { X } from "lucide-react";

import { Input } from "@/components/ui/input";

import { useAccountSearch, type AccountSearchResult } from "../../api";

/**
 * Typeahead over /renewals/accounts. Debounced 250ms; results dropdown lives
 * just below the input. Selecting a result calls onChange and shows the
 * picked value in the input. ESC and clicking the X clear the picked value.
 */
export function AccountSearch({
  value,
  onChange,
  excludeQb,
  placeholder = "Search account name (min 2 chars)…",
  id,
}: {
  value: AccountSearchResult | null;
  onChange: (v: AccountSearchResult | null) => void;
  /** QB ID to filter out — e.g. the source account in a link form. */
  excludeQb?: number;
  placeholder?: string;
  /** Forwarded to the inner input so a <label htmlFor> can associate. */
  id?: string;
}) {
  const [text, setText] = useState(value ? `${value.name} · QB-${value.qb_id}` : "");
  const [debounced, setDebounced] = useState(text);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounce keystrokes by 250ms.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(text), 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [text]);

  // Close dropdown on outside click.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const search = useAccountSearch(value ? "" : debounced);
  const results = (search.data?.results ?? []).filter((r) => r.qb_id !== excludeQb);

  function pick(r: AccountSearchResult) {
    onChange(r);
    setText(`${r.name} · QB-${r.qb_id}`);
    setOpen(false);
  }
  function clear() {
    onChange(null);
    setText("");
    setOpen(true);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          if (value) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="pr-8 text-sm"
        autoComplete="off"
      />
      {value && (
        <button
          type="button"
          onClick={clear}
          title="Clear"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && !value && (search.isFetching || results.length > 0) && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-md">
          {search.isFetching && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
          )}
          {!search.isFetching && results.length === 0 && debounced.length >= 2 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches.</div>
          )}
          {results.map((r) => (
            <button
              key={r.qb_id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r);
              }}
              className="block w-full border-b border-border px-3 py-1.5 text-left text-sm hover:bg-accent"
            >
              <div className="font-medium">{r.name}</div>
              <div className="text-xs text-muted-foreground">QB-{r.qb_id}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
